import { useEffect, useRef, useState } from 'react';
import { useShell } from './ShellProvider';
import { shell } from './shellClasses';

function relativeTime(iso: string | null): string {
  if (!iso) return 'not synced yet';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return 'not synced yet';
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return 'synced just now';
  if (mins < 60) return `synced ${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `synced ${hours} h ago`;
  return `synced ${Math.round(hours / 24)} d ago`;
}

function absoluteTime(iso: string | null): string {
  if (!iso) return 'Unknown';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Single trust surface for the whole app: live state + freshness.
 * Replaces per-widget "certified LIVE" disclaimers and duplicate sync dots.
 */
export function TrustChip() {
  const { context } = useShell();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const live = context.connectionStatus === 'connected';
  const down =
    context.connectionStatus === 'error' || context.connectionStatus === 'disconnected';
  const dot = live
    ? 'bg-[var(--shell-success)]'
    : down
      ? 'bg-[var(--shell-danger)]'
      : 'bg-[var(--shell-fg-subtle)]';
  const state = live ? 'Live' : down ? 'Offline' : 'Checking';
  const freshness = relativeTime(context.lastSync);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Data status: ${state}, ${freshness}. Open details.`}
        className={`inline-flex min-h-[44px] items-center gap-2 rounded-full px-3 text-xs font-medium ${shell.hoverRow} ${shell.focusRing}`}
      >
        <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} aria-hidden />
        <span className="text-[var(--shell-fg)]">{state}</span>
        <span className={`hidden tabular-nums sm:inline ${shell.muted}`}>{freshness}</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Data status details"
          className={`absolute right-0 z-50 mt-1 w-72 rounded-[var(--shell-radius-md)] border ${shell.border} ${shell.surfaceElevated} p-3 text-xs shadow-lg`}
        >
          <dl className="space-y-2">
            <div className="flex justify-between gap-3">
              <dt className={shell.muted}>Store</dt>
              <dd className="truncate font-medium text-[var(--shell-fg)]">
                {context.sellerLabel || '—'}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className={shell.muted}>Marketplace</dt>
              <dd className="truncate font-medium text-[var(--shell-fg)]">
                {context.marketplace || '—'}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className={shell.muted}>Last update</dt>
              <dd className="font-medium tabular-nums text-[var(--shell-fg)]">
                {absoluteTime(context.lastSync)}
              </dd>
            </div>
          </dl>
          <p className={`mt-3 leading-relaxed ${shell.muted}`}>
            Every number on this screen comes from your live store connection. Nothing is
            estimated or filled in.
          </p>
        </div>
      )}
    </div>
  );
}
